import { cardDisplayName, cardPrintedNumber } from '../utils/cardMetadata';
import type { ClassifyPrediction } from '../types';

interface CardCandidateItemProps {
  prediction: ClassifyPrediction;
  rank: number;
  onSelect: (prediction: ClassifyPrediction) => void;
}

function formatLanguage(language?: string) {
  if (!language) return 'Language not provided';
  const labels: Record<string, string> = {
    ja: 'Japanese',
    jp: 'Japanese',
    zh: 'Chinese',
    'zh-TW': 'Traditional Chinese',
    en: 'English',
    ko: 'Korean',
  };
  return labels[language] || language;
}

export default function CardCandidateItem({ prediction, rank, onSelect }: CardCandidateItemProps) {
  const card = prediction.card;
  const confidence = Math.max(0, Math.min(100, Math.round(prediction.confidence * 100)));
  const imageUrl = card?.image_url_at_self_platform || card?.image_url;
  const cardName = cardDisplayName(card) || 'Unlabeled Pokémon card';

  return (
    <article className="group overflow-hidden rounded-[24px] border border-border bg-white shadow-sm transition-all hover:border-cyan/40 hover:shadow-panel">
      <div className="grid min-h-full grid-cols-[112px_minmax(0,1fr)] sm:grid-cols-[150px_minmax(0,1fr)]">
        <div className="relative bg-gradient-to-br from-background-secondary to-white p-2.5 sm:p-3">
          <div className="aspect-[63/88] overflow-hidden rounded-xl border border-border/70 bg-white shadow-sm">
            {imageUrl ? (
              <img src={imageUrl} alt={cardName} className="h-full w-full object-contain" loading="lazy" />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 px-2 text-center text-secondary-light">
                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.6-4.6a2 2 0 0 1 2.8 0L16 16m-2-2 1.6-1.6a2 2 0 0 1 2.8 0L20 14M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z" />
                </svg>
                <span className="text-[9px] font-semibold uppercase tracking-[0.1em]">Image unavailable</span>
              </div>
            )}
          </div>
          {rank === 0 && (
            <span className="absolute left-3 top-3 rounded-full bg-cyan px-2 py-1 text-[8px] font-extrabold uppercase tracking-[0.12em] text-ink sm:left-4 sm:top-4">
              Best match
            </span>
          )}
        </div>

        <div className="flex min-w-0 flex-col p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="eyebrow text-secondary-light">Candidate {String(rank + 1).padStart(2, '0')}</p>
              <h3 className="mt-2 line-clamp-2 text-base font-bold leading-5 tracking-[-0.025em] text-primary sm:text-lg sm:leading-6">{cardName}</h3>
            </div>
            <div className="flex-shrink-0 text-right">
              <span className="price-numeral text-sm font-bold text-primary">{Number.isFinite(confidence) ? `${confidence}%` : 'Text'}</span>
              <span className="block text-[9px] font-semibold uppercase tracking-[0.08em] text-secondary-light">match</span>
            </div>
          </div>

          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            <div className="rounded-xl bg-background-secondary px-3 py-2.5">
              <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-secondary-light">Printed number</span>
              <span className="mt-1 block truncate font-mono text-xs font-semibold text-primary">{cardPrintedNumber(card) || 'Not provided'}</span>
            </div>
            <div className="rounded-xl bg-background-secondary px-3 py-2.5">
              <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-secondary-light">Card language</span>
              <span className="mt-1 block truncate text-xs font-semibold text-primary">{formatLanguage(card?.language)}</span>
            </div>
          </div>

          {card?.series_code && <p className="mt-2 text-xs text-secondary">{card.series_code}</p>}
          {card?.type && (
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="rounded-full border border-border px-2.5 py-1 text-[10px] font-semibold text-secondary">{card.type}</span>
            </div>
          )}

          <div className="mt-4 border-t border-border/70 pt-3">
            <p className="truncate font-mono text-[9px] text-secondary-light" title={prediction.id_label}>Resolver ID · {prediction.id_label}</p>
            <div className="mt-2 h-1 overflow-hidden rounded-full bg-background-secondary" aria-hidden="true">
              <div className="h-full rounded-full bg-cyan" style={{ width: `${Number.isFinite(confidence) ? confidence : 0}%` }} />
            </div>
          </div>

          <button
            type="button"
            onClick={() => onSelect(prediction)}
            className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-ink px-4 py-2.5 text-xs font-bold text-white transition-colors hover:bg-slate"
            aria-label={`Confirm ${cardName}`}
          >
            Confirm this card
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>
    </article>
  );
}
